import dayjs, { Dayjs } from 'dayjs';
import * as isToday from 'dayjs/plugin/isToday';
import * as isTomorrow from 'dayjs/plugin/isTomorrow';
import * as isYesterday from 'dayjs/plugin/isYesterday';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as weekday from 'dayjs/plugin/weekday';
import { i18next } from '../i18n';

dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);
dayjs.extend(weekday);
dayjs.extend(utc);
dayjs.extend(timezone);

export const getToday = () => dayjs().hour(0).minute(0).second(0).millisecond(0);
export const dateSorter = (d1: string, d2: string) => (!d1 ? -1 : !d2 ? 1 : dayjs(d1).isAfter(dayjs(d2)) ? 1 : -1);

export const getWeekRange = (date: Dayjs) => {
    // Return a week from the given date to the future (7 days ahead)
    const start = date.hour(0).minute(0).second(0).millisecond(0);
    const end = date.add(7, 'days').hour(23).minute(59).second(59).millisecond(999);
    return {
        start: start.toISOString(),
        end: end.toISOString(),
    };
};
export const isTodayOrEarlier = (d: string) => dayjs(d).isBefore(dayjs().add(1, 'day').hour(0).minute(0).second(0).millisecond(0));
export const dayjsStartOfDay = (d: dayjs.Dayjs) => d.hour(0).minute(0).second(0).millisecond(0);
export const dateSorterGeneric = <T>(a: T, b: T, propGetter: (item: T) => string, ascending: boolean = true) =>
    dayjs(propGetter(a)).isAfter(dayjs(propGetter(b))) ? (ascending ? 1 : -1) : ascending ? -1 : 1;

export function msSplit(duration: number) {
    const milliseconds = Math.floor((duration % 1000) / 100),
        seconds = Math.floor((duration / 1000) % 60),
        minutes = Math.floor((duration / (1000 * 60)) % 60),
        hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    return { milliseconds, seconds, minutes, hours };
}

export function msToTime(duration: number) {
    const { seconds, minutes, hours } = msSplit(duration);

    const hoursStr = hours < 10 ? '0' + hours : hours;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const secondsStr = seconds < 10 ? '0' + seconds : seconds;

    return (hoursStr !== '00' ? hoursStr + ':' : '') + minutesStr + ':' + secondsStr;
}

export function msToShortTime(duration: number) {
    const { minutes, hours } = msSplit(duration);

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (hours > 0) {
        return `${hours}h`;
    }
    return `${minutes}m`;
}

export const timeToMs = (minutes: number): number => minutes * 60 * 1000;

export const formatMMSS = (seconds: number) =>
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

export function toCalendarString(date: Date) {
    return dayjs(date).format('YYYY-MM-DDTHH:mm');
}

export function toDueTime(date: dayjs.Dayjs) {
    if (date.hour() === 0 && date.minute() === 0) return '';
    return date.format('HH:mm');
}

const formatFullDate = (date: dayjs.Dayjs) => {
    const today = dayjs();
    const yearDiff = date.diff(today, 'year');
    if (yearDiff !== 0) return date.format('D MMM YYYY');
    return date.format('D MMM');
};

export function toDueDate(date: dayjs.Dayjs, lowercase: boolean = true) {
    if (date.isYesterday()) return i18next.t(lowercase ? 'dates.yesterday' : 'dates.yesterdayTitle');
    if (date.isToday()) return i18next.t(lowercase ? 'dates.today' : 'dates.todayTitle');
    if (date.isTomorrow()) return i18next.t(lowercase ? 'dates.tomorrow' : 'dates.tomorrowTitle');

    return toDueDateSimple(date, lowercase);
}

export function toDueDateSimple(date: dayjs.Dayjs, lowercase: boolean = true) {
    const today = dayjsStartOfDay(dayjs());
    const dayDiff = date.diff(today, 'day');

    if (dayDiff < 0) return formatFullDate(date);
    if (dayDiff < 7) {
        const weekday = date.format('dddd');
        return lowercase ? weekday.toLocaleLowerCase(i18next.language) : weekday;
    }
    return formatFullDate(date);
}

export function toDueString(dueDate: dayjs.Dayjs, lowercase: boolean = true) {
    const dueTime = toDueTime(dueDate);
    return `${toDueDate(dueDate, lowercase)}${dueTime ? ' ' + dueTime : ''}`;
}

export function toDueStringSimple(dueDate: dayjs.Dayjs, lowercase: boolean = true) {
    const dueTime = toDueTime(dueDate);
    return `${toDueDateSimple(dueDate, lowercase)}${dueTime ? ' ' + dueTime : ''}`;
}

export function toRelativeDayLabel(date: dayjs.Dayjs): string {
    const dayDiff = dayjsStartOfDay(date).diff(dayjsStartOfDay(dayjs()), 'day');

    if (dayDiff === 0) return i18next.t('dates.todayTitle');
    if (dayDiff === 1) return i18next.t('dates.tomorrowTitle');
    if (dayDiff === -1) return i18next.t('dates.yesterdayTitle');
    if (dayDiff > 1) return i18next.t('dates.inDays', { count: dayDiff });
    return i18next.t('dates.daysAgo', { count: Math.abs(dayDiff) });
}

export function getUserTimezone(): string {
    return dayjs.tz.guess();
}

export function localTimeToUtcIso(iso: string, tz?: string): string {
    const trimmed = iso.trim();
    const hasExplicitZone = /Z$/i.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed) || /[+-]\d{4}$/.test(trimmed);
    if (hasExplicitZone) {
        return dayjs(trimmed).toISOString();
    }
    const zone = tz ?? dayjs.tz.guess();
    const naive = trimmed.replace(/\.\d{3}Z?$/i, '').replace('Z', '');
    return dayjs.tz(naive, zone).toISOString();
}

export function formatAchievementDate(date: string | Date): string {
    const achievementDate = dayjs(date);
    if (achievementDate.isToday()) return i18next.t('dates.today');
    if (achievementDate.isYesterday()) return i18next.t('dates.yesterday');
    return achievementDate.format('DD/MM/YYYY');
}
